/**
 * A task's categories — Aaron, Oct 25, note 1.5 ("colored labels that can be
 * named by user and applied to tasks … Task categories and email categories
 * are different") and Oct 26, answer 1 ("one per task list. any user on the
 * list can edit"); `docs/activity.md` §8.7.
 *
 * The Inbox's categories, the same furniture (`../categories.tsx`), in
 * **sets**: a task in a list wears its list's, which anyone on the list may
 * make, rename or remove; a task on no list wears the company's, which
 * anybody may (0144, as built). A category on a shared task reads the same to
 * everybody on it, as Planner's labels do, so removing one asks first — it
 * comes off every task that wears it. A task moved to another list keeps
 * what it wears: the server carries each into the new list's set (0148).
 *
 * `TaskCategoriesField` is the Task sheet's and New task's: the chips a task
 * wears and **Add a category**, which opens the set as ticks with
 * **New category…** and **Edit categories…** under them — a popover on a
 * desktop, a sheet on a phone.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PanelSheet } from '../../components/panel-sheet.js'
import { Popover } from '../../components/overlays.js'
import { markInk } from '../../frame/mark-glyphs.js'
import { cn } from '../../lib/cn.js'
import { usePhone } from '../../lib/use-narrow.js'
import { activityRequest } from '../api.js'
import {
  CategoriesEditor, CategoryChip, CategoryDialog, CategoryMenuRow, type Category, type CategorySet,
} from '../categories.js'
import { Glyph } from '../glyphs.js'
import { BackLayer } from '../use-activity-history.js'

/** The company's set — what a task on no list wears (§8.7). */
export const TASK_CATEGORIES: CategorySet = {
  endpoint: '/activity/task-categories',
  dialogSub: 'Everybody sees the company’s task categories on the tasks they can see.',
  editorTitle: 'Task categories',
  editorSub: 'The company’s set, for tasks on no list, so a category on a shared task means the same to everybody on it. A task list keeps its own.',
  placeholder: 'Waiting on a permit',
  removeConfirm: () => 'It comes off every task that wears it, for everybody. The tasks themselves stay as they are.',
  /* Over the Task sheet (50) and New task (50, or 60 on a phone) and their pickers (70). */
  layers: { editor: 75, dialog: 80 },
}

/**
 * A task list's own set (Aaron, Oct 26, answer 1): the same furniture, at
 * the list's address and in the list's words.
 */
export function listCategorySet(list: { id: string; name: string }): CategorySet {
  return {
    endpoint: `/activity/task-lists/${list.id}/categories`,
    dialogSub: `Everybody on ${list.name} sees these on its tasks.`,
    editorTitle: 'Task categories',
    editorSub: `${list.name}’s own set: anyone on the list can make, rename or remove one, and its tasks wear these.`,
    placeholder: TASK_CATEGORIES.placeholder,
    removeConfirm: () => `It comes off every task in ${list.name} that wears it, for everybody. The tasks themselves stay as they are.`,
    layers: TASK_CATEGORIES.layers,
  }
}

/**
 * Something about a set of task categories changed — made, renamed,
 * recoloured or removed: every screen holding them asks again, and the Board
 * and the List reload their chips.
 */
export const TASK_CATEGORIES_EVENT = 'rowted:task-categories-changed'

/** A category's set, as the server says it: its list's id, or `null` for the company's. */
const setIdOf = (category: Category): string | null | undefined => (category as Category & { listId?: string | null }).listId

/**
 * One set of task categories — a list's when `listId` is given, else the
 * company's — asked for once per screen and again when any set changes.
 * `taskId` lets one of a task's people read its list's set though they are
 * not on the list. `canEdit` is whether this viewer may make, rename or
 * remove one (anyone on the list; anybody, for the company's).
 */
export function useTaskCategories(listId: string | null = null, taskId?: string): {
  categories: Category[]; reload: () => void; loaded: boolean; canEdit: boolean; set: CategorySet
} {
  const [categories, setCategories] = useState<Category[]>([])
  const [loaded, setLoaded] = useState(false)
  const [canEdit, setCanEdit] = useState(true)
  const [list, setList] = useState<{ id: string; name: string } | null>(null)
  const load = useCallback(() => {
    const path = listId
      ? `/activity/task-lists/${listId}/categories${taskId ? `?task=${taskId}` : ''}`
      : TASK_CATEGORIES.endpoint
    activityRequest<{ items: Category[]; canEdit?: boolean; list?: { id: string; name: string } | null }>(path)
      .then((answer) => {
        setCategories(Array.isArray(answer?.items) ? answer.items : [])
        setCanEdit(answer?.canEdit !== false)
        setList(answer?.list ?? null)
        setLoaded(true)
      })
      .catch(() => { setCategories([]); setCanEdit(false); setLoaded(true) })
  }, [listId, taskId])
  useEffect(() => {
    setLoaded(false)
    load()
    window.addEventListener(TASK_CATEGORIES_EVENT, load)
    return () => window.removeEventListener(TASK_CATEGORIES_EVENT, load)
  }, [load])
  const reload = useCallback(() => { window.dispatchEvent(new CustomEvent(TASK_CATEGORIES_EVENT)) }, [])
  const set = useMemo(() => (listId && list?.id === listId ? listCategorySet(list) : TASK_CATEGORIES), [listId, list])
  return { categories, reload, loaded, canEdit: listId ? canEdit && list?.id === listId : true, set }
}

/** Up to `max` chips, then *+n* — a card's and a row's. */
export function TaskCategoryChips({
  categories, max = 2, size = 'small',
}: { categories: readonly Category[]; max?: number; size?: 'row' | 'small' }) {
  if (categories.length === 0) return null
  const shown = categories.slice(0, max)
  const more = categories.length - shown.length
  return (
    <span className="inline-flex min-w-0 shrink items-center gap-1 overflow-hidden" data-task-categories>
      {shown.map((category) => <CategoryChip key={category.id} category={category} size={size} />)}
      {more > 0 && (
        <span className="shrink-0 text-[11px] font-semibold text-text-faint"
          title={categories.slice(max).map((category) => category.name).join(', ')}>+{more}</span>
      )}
    </span>
  )
}

/**
 * The field: the chips the task wears and **Add a category**, which offers
 * the set of the task's list (`listId`) — or the company's, for a task on no
 * list. `onChange` gets the whole set, never a delta. Read-only on a
 * finished task.
 */
export function TaskCategoriesField({
  value, onChange, readOnly = false, label = 'Categories', height = 38, listId = null, taskId,
}: {
  value: readonly Category[]
  /** The whole set the task now wears, in the order it was picked. */
  onChange: (categories: Category[]) => void
  readOnly?: boolean
  label?: string
  /** The box's height: the Task sheet's 38, New task's 36, New task's phone boxes' 46. */
  height?: 36 | 38 | 46
  /** The task's list (as the sheet has it now), whose set is offered; `null` for the company's. */
  listId?: string | null
  /** The task, so one of its people who is not on its list may still read the list's set. */
  taskId?: string
}) {
  const phone = usePhone()
  const { categories, reload, loaded, canEdit, set } = useTaskCategories(listId, taskId)
  const [open, setOpen] = useState(false)
  const [naming, setNaming] = useState(false)
  const [editing, setEditing] = useState(false)
  const anchor = useRef<HTMLButtonElement>(null)
  const ids = value.map((category) => category.id)
  /*
   * The chips follow the set, so a rename or a removal elsewhere shows here at once. A chip from
   * another set — the task's list was just changed here — stays: Save carries it into the new list's.
   */
  const live = new Map(categories.map((category) => [category.id, category]))
  const ofThisSet = (category: Category): boolean => {
    const own = setIdOf(category)
    return own === undefined || (own ?? null) === (listId ?? null)
  }
  const worn = value.map((category) => live.get(category.id) ?? category)
    .filter((category) => !loaded || categories.length === 0 || live.has(category.id) || !ofThisSet(category))

  /*
   * The list changed under chips from the old one's set: each takes the new set's of the same name,
   * where it has one, so the picker ticks it rather than offering a twin. Not what was already so
   * when the field opened (a task whose list has been removed): that is the task as saved, untouched.
   */
  const asOpened = useRef<Set<string> | null>(null)
  useEffect(() => {
    if (readOnly || !loaded) return
    if (asOpened.current === null) {
      asOpened.current = new Set(value.filter((category) => !ofThisSet(category)).map((category) => category.id))
      return
    }
    const byName = new Map(categories.map((category) => [category.name.trim().toLowerCase(), category]))
    let moved = false
    const next = value.map((category) => {
      if (ofThisSet(category) || live.has(category.id) || asOpened.current?.has(category.id)) return category
      const twin = byName.get(category.name.trim().toLowerCase())
      if (!twin) return category
      moved = true
      return twin
    })
    if (moved) onChange(next.filter((category, at) => next.findIndex((one) => one.id === category.id) === at))
    // The set's answer and the list it is for; `value` is read as it stands then.
  }, [categories, loaded, listId])

  const toggle = (category: Category) => {
    onChange(ids.includes(category.id) ? worn.filter((one) => one.id !== category.id) : [...worn, category])
  }

  const rows = (
    <>
      {categories.length === 0 && (
        <div className={cn('px-3 text-text-muted', phone ? 'py-3 text-[14px]' : 'py-2 text-[12.5px]')}>No categories yet.</div>
      )}
      {categories.map((category) => {
        const on = ids.includes(category.id)
        return phone ? (
          <button key={category.id} type="button" role="menuitemcheckbox" aria-checked={on} onClick={() => toggle(category)}
            className="flex h-[52px] w-full items-center gap-3 border-b border-line-faint px-4 text-left text-[15px] font-medium">
            <i aria-hidden className="block h-3 w-3 shrink-0 rounded-full" style={{ background: markInk(category.colour) }} />
            <span className="flex-1 truncate">{category.name}</span>
            {on && <span className="inline-flex text-accent"><Glyph name="check" size={18} /></span>}
          </button>
        ) : (
          <CategoryMenuRow key={category.id} role="menuitemcheckbox" on={on} swatch={category.colour}
            onClick={() => toggle(category)}>{category.name}</CategoryMenuRow>
        )
      })}
    </>
  )
  /* Making, renaming and removing are for anyone on the list (Aaron, Oct 26) — absent, not greyed, for the rest. */
  const actions = !canEdit ? null : phone ? (
    <>
      <SheetAction glyph="plus" label="New category…" onClick={() => { setOpen(false); setNaming(true) }} />
      {categories.length > 0 && (
        <SheetAction glyph="pencil" label="Edit categories…" onClick={() => { setOpen(false); setEditing(true) }} />
      )}
    </>
  ) : (
    <>
      <div className="my-1 border-t border-line-soft" />
      <CategoryMenuRow on={false} onClick={() => { setOpen(false); setNaming(true) }}>New category…</CategoryMenuRow>
      {categories.length > 0 && (
        <CategoryMenuRow on={false} onClick={() => { setOpen(false); setEditing(true) }}>Edit categories…</CategoryMenuRow>
      )}
    </>
  )

  return (
    <div data-task-categories-field>
      <div className={cn('flex flex-wrap items-center gap-1.5 border border-line bg-surface px-1.5 py-1',
        height === 46 ? 'rounded-[10px]' : 'rounded-lg')} style={{ minHeight: height }}>
        {worn.map((category) => (
          <span key={category.id} className="inline-flex items-center gap-0.5">
            <CategoryChip category={category} />
            {!readOnly && (
              <button type="button" aria-label={`Take ${category.name} off`} onClick={() => toggle(category)}
                className="inline-flex h-5 w-5 items-center justify-center rounded text-text-muted hover:text-text">
                <Glyph name="close" size={11} />
              </button>
            )}
          </span>
        ))}
        {worn.length === 0 && readOnly && <span className="px-1.5 text-[12.5px] text-text-faint">None</span>}
        {!readOnly && (
          <button ref={anchor} type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((was) => !was)}
            className="inline-flex h-7 items-center gap-1 px-1.5 text-[12px] font-semibold text-accent">
            <Glyph name="tag" size={13} />{worn.length === 0 ? 'Add a category' : 'Add'}
          </button>
        )}
      </div>
      {open && !phone && (
        <Popover open anchor={anchor.current} onClose={() => setOpen(false)} width={240} label={label} className="py-1.5" layer={70}>
          {rows}
          {actions}
        </Popover>
      )}
      {open && phone && (
        <>
          {/* Back closes the picker before the sheet under it (Aaron, Oct 26). */}
          <BackLayer onBack={() => setOpen(false)} />
          <PanelSheet open onClose={() => setOpen(false)} title={label} layer={70}>
            {rows}
            {actions}
          </PanelSheet>
        </>
      )}
      {naming && (
        <CategoryDialog set={set} category={null} onClose={() => setNaming(false)}
          onSaved={(made) => { reload(); onChange([...worn, made]) }} />
      )}
      {editing && (
        <CategoriesEditor set={set} categories={categories} onClose={() => setEditing(false)}
          onChanged={reload} />
      )}
    </div>
  )
}

function SheetAction({ glyph, label, onClick }: { glyph: 'plus' | 'pencil'; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className="flex h-[52px] w-full items-center gap-3 border-b border-line-faint px-4 text-left text-[15px] font-medium">
      <span className="inline-flex text-text-muted"><Glyph name={glyph} size={18} /></span>{label}
    </button>
  )
}
